/**
 * React Query hook for linking a trace to a security issue.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { linkTraceToIssue } from "@/services/securityIssuesService";
import { ApiError } from "@/services/api";

export function useLinkTrace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ issueId, traceId }: { issueId: number; traceId: number }) =>
      linkTraceToIssue(issueId, traceId),
    onSettled: (_data, error, variables) => {
      if (error) {
        // 409 means already linked — treat as info, not error
        if (error instanceof ApiError && error.status === 409) {
          toast.info(`Trace #${variables.traceId} is already linked`);
        } else {
          toast.error(error instanceof ApiError ? error.message : "Failed to link trace");
        }
      } else {
        toast.success(`Trace #${variables.traceId} linked to issue`);
      }
      // Invalidate activities so History tab picks up the TRACE_LINKED entry
      queryClient.invalidateQueries({
        queryKey: ["issue-activities"],
      });
      // Invalidate cached search results so already_linked flags update
      queryClient.invalidateQueries({
        queryKey: ["latest-action", variables.issueId, "search_similar_prompts"],
      });
    },
  });
}
